import type {
  GameCommand,
  GameCommandResult,
  PlayerSessionView,
  RoomView,
} from "@secret-cabinet/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";

interface ServerToClientEvents {
  "room:view": (view: RoomView) => void;
  "room:error": (error: SocketError) => void;
  "room:afk-warning": (value: { remainingMs: number }) => void;
  "room:afk-evicted": (value: { reason: "inactivity" }) => void;
  "room:presence-required": (value: { reason: "inactivity" | "action-pending" }) => void;
}

interface ClientToServerEvents {
  "game:command": (
    envelope: { actionId: string; command: GameCommand },
    acknowledge: (result: GameCommandResult) => void,
  ) => void;
  "player:still-here": (acknowledge: (result: GameCommandResult) => void) => void;
}

interface SocketError {
  code: string;
  message: string;
}

export type ConnectionStatus = "connecting" | "live" | "offline";

export function useRoomSocket(
  code: string,
  mode: "player" | "tv",
  session: PlayerSessionView | null,
): {
  view: RoomView | null;
  status: ConnectionStatus;
  error: SocketError | null;
  retryConnection: () => void;
  sendCommand: (command: GameCommand) => Promise<GameCommandResult>;
  afkWarningMs: number | null;
  presenceRequired: boolean;
  confirmPresence: () => Promise<GameCommandResult>;
} {
  const [view, setView] = useState<RoomView | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [error, setError] = useState<SocketError | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [afkWarningMs, setAfkWarningMs] = useState<number | null>(null);
  const [presenceRequired, setPresenceRequired] = useState(false);
  const socketRef = useRef<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);

  useEffect(() => {
    setView(null);
    if (mode === "player" && !session) {
      setStatus("offline");
      setError({ code: "missing-session", message: "Join this room before reconnecting." });
      return;
    }
    const socketUrl = import.meta.env.VITE_SOCKET_URL || window.location.origin;
    const socket = io(socketUrl, {
      auth:
        mode === "tv"
          ? { mode, roomCode: code }
          : {
              mode,
              roomCode: code,
              playerId: session!.id,
              token: session!.token,
            },
      transports: ["websocket", "polling"],
      reconnection: true,
    });
    socketRef.current = socket;
    setStatus("connecting");
    setError(null);

    socket.on("connect", () => {
      setStatus("live");
      setError(null);
    });
    socket.on("disconnect", () => setStatus("offline"));
    socket.on("connect_error", (connectionError) => {
      const data = connectionError as Error & { data?: { code?: string } };
      const code = data.data?.code ?? "connection-failed";
      setStatus("offline");
      setError({
        code,
        message: connectionError.message || "The live room connection failed.",
      });
      if (isTerminalSessionError(code)) {
        socket.io.opts.reconnection = false;
      }
    });
    socket.on("room:error", (roomError) => {
      setStatus("offline");
      setError(roomError);
      if (isTerminalSessionError(roomError.code)) {
        socket.io.opts.reconnection = false;
      }
    });
    socket.on("room:view", (nextView) => {
      setView((current) =>
        current && current.public.revision > nextView.public.revision ? current : nextView,
      );
    });
    socket.on("room:afk-warning", ({ remainingMs }) => setAfkWarningMs(remainingMs));
    socket.on("room:presence-required", () => setPresenceRequired(true));
    socket.on("room:afk-evicted", () => {
      setAfkWarningMs(null);
      setPresenceRequired(true);
      setError({ code: "afk-evicted", message: "Your seat was marked away after no response." });
    });

    return () => {
      socketRef.current = null;
      socket.disconnect();
    };
  }, [code, mode, retryNonce, session?.id, session?.token]);

  const retryConnection = useCallback(() => {
    setRetryNonce((current) => current + 1);
  }, []);

  const sendCommand = useCallback((command: GameCommand): Promise<GameCommandResult> => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      return Promise.resolve({
        ok: false,
        code: "connection-offline",
        message: "Reconnect before taking that action.",
      });
    }
    return new Promise((resolve) => {
      let finished = false;
      const timeout = window.setTimeout(() => {
        if (!finished) {
          finished = true;
          resolve({
            ok: false,
            code: "command-timeout",
            message: "The chamber did not confirm that action. Wait for the live state before retrying.",
          });
        }
      }, 8_000);
      socket.emit(
        "game:command",
        { actionId: window.crypto.randomUUID(), command },
        (result) => {
          if (!finished) {
            finished = true;
            window.clearTimeout(timeout);
            resolve(result);
          }
        },
      );
    });
  }, []);

  const confirmPresence = useCallback((): Promise<GameCommandResult> => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      return Promise.resolve({ ok: false, code: "connection-offline", message: "Reconnect before confirming your seat." });
    }
    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => {
        resolve({ ok: false, code: "command-timeout", message: "The chamber did not confirm your presence. Try again." });
      }, 8_000);
      socket.emit("player:still-here", (result) => {
        window.clearTimeout(timeout);
        if (result.ok) {
          setPresenceRequired(false);
          setAfkWarningMs(null);
          setError(null);
          setStatus("live");
        }
        resolve(result);
      });
    });
  }, []);

  return { view, status, error, retryConnection, sendCommand, afkWarningMs, presenceRequired, confirmPresence };
}

function isTerminalSessionError(code: string): boolean {
  return code === "invalid-session" || code === "room-not-found";
}
